import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import { MdExpandMore, MdCheckCircle, MdError, MdInfo, MdWarning } from 'react-icons/md';
import { cteAPI } from 'services/apiRest';
import { Container, EventoCard, StatusBadge, InfoRow, InfoLabel, InfoValue, MensagemBox } from './styles';

function ModalEventosCte({ open, onClose, viagemId }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && viagemId) {
      carregarEventos(false); // Carregar sem sincronizar na abertura
    }
  }, [open, viagemId]);

  const carregarEventos = async (sincronizar = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Se sincronizar, primeiro buscar o último evento para obter o cte_id
      if (sincronizar) {
        try {
          const ultimoEvento = await cteAPI.buscarUltimoEvento(viagemId);
          if (ultimoEvento && ultimoEvento.cte_id) {
            // Consultar CT-e diretamente na API Nuvem Fiscal usando GET /cte/:id
            // O id é o ID único do evento gerado pela API (cte_id)
            // O backend já atualiza automaticamente o evento no banco após a consulta
            await cteAPI.consultarCte(ultimoEvento.cte_id);
          } else {
            console.warn('Nenhum evento encontrado com cte_id para consultar');
          }
        } catch (syncErr) {
          console.warn('Erro ao consultar CT-e na SEFAZ (continuando com busca local):', syncErr);
          // Continuar mesmo se a consulta falhar
        }
      }
      
      // Buscar eventos atualizados do banco
      const dados = await cteAPI.buscarEventosPorViagem(viagemId);
      setEventos(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error('Erro ao carregar eventos:', err);
      setError('Erro ao carregar eventos. Tente novamente.');
      setEventos([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'autorizado') return '#4caf50';
    if (statusLower === 'rejeitado' || statusLower === 'cancelado') return '#f44336';
    if (statusLower === 'transmitido' || statusLower === 'enviado') return '#2196f3';
    return '#ff9800';
  };

  const getStatusIcon = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'autorizado') return <MdCheckCircle size={20} />;
    if (statusLower === 'rejeitado' || statusLower === 'cancelado') return <MdError size={20} />;
    if (statusLower === 'transmitido' || statusLower === 'enviado') return <MdInfo size={20} />;
    return <MdWarning size={20} />;
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      const date = new Date(data);
      return format(date, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
    } catch {
      return data;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Eventos do CT-e</Typography>
          <Box display="flex" gap={1}>
            <Button 
              onClick={() => carregarEventos(false)} 
              size="small" 
              variant="outlined" 
              disabled={loading}
            >
              Recarregar
            </Button>
            <Button 
              onClick={() => carregarEventos(true)} 
              size="small" 
              variant="contained" 
              style={{ marginLeft: '10px',backgroundColor: '#122d58', color: '#fff' }}  
              disabled={loading}
            >
              Consultar SEFAZ
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Container>
          {loading && (
            <Box textAlign="center" py={4}>
              <Typography>Carregando eventos...</Typography>
            </Box>
          )}

          {error && (
            <Box textAlign="center" py={4}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {!loading && !error && eventos.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                Nenhum evento encontrado para esta viagem.
              </Typography>
            </Box>
          )}

          {!loading && !error && eventos.length > 0 && (
            <Box>
              {eventos.map((evento, index) => (
                <EventoCard key={evento.id || index} elevation={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StatusBadge
                        style={{
                          backgroundColor: getStatusColor(evento.status),
                          color: '#fff',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getStatusIcon(evento.status)}
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {evento.status || 'Desconhecido'}
                          </Typography>
                        </Box>
                      </StatusBadge>
                      {evento.ambiente && (
                        <Chip
                          label={evento.ambiente === 'homologacao' ? 'Homologação' : 'Produção'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {formatarData(evento.created_at)}
                    </Typography>
                  </Box>

                  <Divider style={{ margin: '12px 0' }} />

                  <Box>
                    <InfoRow>
                      <InfoLabel>Data de Emissão:</InfoLabel>
                      <InfoValue>{formatarData(evento.data_emissao)}</InfoValue>
                    </InfoRow>

                    {evento.chave && (
                      <InfoRow>
                        <InfoLabel>Chave de Acesso:</InfoLabel>
                        <InfoValue style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {evento.chave}
                        </InfoValue>
                      </InfoRow>
                    )}

                    {evento.numero_protocolo && (
                      <InfoRow>
                        <InfoLabel>Protocolo:</InfoLabel>
                        <InfoValue>{evento.numero_protocolo}</InfoValue>
                      </InfoRow>
                    )}

                    {evento.numero && (
                      <InfoRow>
                        <InfoLabel>Número do CT-e:</InfoLabel>
                        <InfoValue>{evento.numero}</InfoValue>
                      </InfoRow>
                    )}

                    {evento.serie && (
                      <InfoRow>
                        <InfoLabel>Série:</InfoLabel>
                        <InfoValue>{evento.serie}</InfoValue>
                      </InfoRow>
                    )}

                    {evento.valor_total && (
                      <InfoRow>
                        <InfoLabel>Valor Total:</InfoLabel>
                        <InfoValue>
                          {Number(evento.valor_total).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </InfoValue>
                      </InfoRow>
                    )}

                    {evento.codigo_status !== null && evento.codigo_status !== undefined && (
                      <InfoRow>
                        <InfoLabel>Código de Status:</InfoLabel>
                        <InfoValue>{evento.codigo_status}</InfoValue>
                      </InfoRow>
                    )}

                    {evento.motivo_status && (
                      <InfoRow>
                        <InfoLabel>Motivo:</InfoLabel>
                        <InfoValue>{evento.motivo_status}</InfoValue>
                      </InfoRow>
                    )}

                    {(evento.mensagem || evento.autorizacao?.mensagem) && (
                      <Box mt={2}>
                        <Accordion>
                          <AccordionSummary expandIcon={<MdExpandMore />}>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                              Mensagem da SEFAZ
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <MensagemBox>
                              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                {evento.mensagem || evento.autorizacao?.mensagem || '-'}
                              </Typography>
                            </MensagemBox>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}

                    {evento.autorizacao && (
                      <Box mt={2}>
                        <Accordion>
                          <AccordionSummary expandIcon={<MdExpandMore />}>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                              Detalhes da Autorização
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box>
                              {evento.autorizacao.codigo_status !== null &&
                                evento.autorizacao.codigo_status !== undefined && (
                                  <InfoRow>
                                    <InfoLabel>Código:</InfoLabel>
                                    <InfoValue>{evento.autorizacao.codigo_status}</InfoValue>
                                  </InfoRow>
                                )}
                              {evento.autorizacao.motivo_status && (
                                <InfoRow>
                                  <InfoLabel>Motivo:</InfoLabel>
                                  <InfoValue>{evento.autorizacao.motivo_status}</InfoValue>
                                </InfoRow>
                              )}
                              {evento.autorizacao.numero_protocolo && (
                                <InfoRow>
                                  <InfoLabel>Protocolo:</InfoLabel>
                                  <InfoValue>{evento.autorizacao.numero_protocolo}</InfoValue>
                                </InfoRow>
                              )}
                              {evento.autorizacao.data_recebimento && (
                                <InfoRow>
                                  <InfoLabel>Data de Recebimento:</InfoLabel>
                                  <InfoValue>{formatarData(evento.autorizacao.data_recebimento)}</InfoValue>
                                </InfoRow>
                              )}
                              {evento.autorizacao.chave_acesso && (
                                <InfoRow>
                                  <InfoLabel>Chave de Acesso:</InfoLabel>
                                  <InfoValue style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {evento.autorizacao.chave_acesso}
                                  </InfoValue>
                                </InfoRow>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}

                    {evento.raw_response && (
                      <Box mt={2}>
                        <Accordion>
                          <AccordionSummary expandIcon={<MdExpandMore />}>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                              Resposta Completa da API
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <MensagemBox>
                              <pre style={{ fontSize: '0.75rem', overflow: 'auto', margin: 0 }}>
                                {JSON.stringify(evento.raw_response, null, 2)}
                              </pre>
                            </MensagemBox>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Box>
                </EventoCard>
              ))}
            </Box>
          )}
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalEventosCte;

